'use client';

import { useEffect, useState } from 'react';
import { Shield, LogOut, Loader2 } from 'lucide-react';
import { useAuthStore } from '@/features/auth/stores/auth-store';
import { api } from '@/shared/services/api';

interface ImpersonationInfo {
  isAdmin: boolean;
  originalAdminEmail: string;
  impersonatedUserEmail: string;
}

export function ImpersonationBanner() {
  const [impersonation, setImpersonation] = useState<ImpersonationInfo | null>(null);
  const [exiting, setExiting] = useState(false);
  const user = useAuthStore((s) => s.user);

  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const isImpersonation = urlParams.get('impersonation') === '1';
    const adminEmail = urlParams.get('admin_email');

    if (isImpersonation && adminEmail && user?.email) {
      const info: ImpersonationInfo = {
        isAdmin: true,
        originalAdminEmail: adminEmail,
        impersonatedUserEmail: user.email,
      };
      localStorage.setItem('admin_impersonation', JSON.stringify(info));
      setImpersonation(info);
      window.history.replaceState({}, '', window.location.pathname);
      return;
    }

    const stored = localStorage.getItem('admin_impersonation');
    if (stored) {
      try {
        setImpersonation(JSON.parse(stored) as ImpersonationInfo);
      } catch {
        localStorage.removeItem('admin_impersonation');
      }
    }
  }, [user]);

  const handleExit = async () => {
    setExiting(true);
    try {
      // Restore admin session from saved cookies
      await api.post('/auth/admin-restore');
    } catch (err) {
      console.error('Error restoring admin session:', err);
    }
    localStorage.removeItem('admin_impersonation');
    // Full redirect to admin — forces React state reset with restored admin cookies
    window.location.href = '/admin/users';
  };

  if (!impersonation) return null;

  return (
    <div className="bg-gradient-to-r from-blue-600 to-indigo-600 text-white sticky top-0 z-[100]">
      <div className="mx-auto px-4 py-2.5">
        <div className="flex items-center justify-between gap-4">
          <div className="flex items-center gap-3 flex-1">
            <div className="bg-white/20 p-1.5 rounded-lg">
              <Shield className="h-4 w-4" />
            </div>
            <div className="flex-1">
              <p className="font-semibold text-sm">
                Vizualizezi contul: <span className="font-bold">{impersonation.impersonatedUserEmail}</span>
              </p>
              <p className="text-[0.6875rem] text-blue-100">
                Conectat ca admin ({impersonation.originalAdminEmail}). Admin Panel-ul este deschis in celalalt tab.
              </p>
            </div>
          </div>
          <button
            onClick={handleExit}
            disabled={exiting}
            className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-white text-blue-600 hover:bg-blue-50 text-sm font-medium transition-colors disabled:opacity-50"
          >
            {exiting ? (
              <Loader2 className="h-4 w-4 animate-spin" />
            ) : (
              <LogOut className="h-4 w-4" />
            )}
            {exiting ? 'Se restaureaza...' : 'Iesire din cont'}
          </button>
        </div>
      </div>
    </div>
  );
}
